import React, { useState, useEffect } from "react";
import "./ExamTimer.css";
import WarningDialog from "./WarningDialog";

const ExamTimer = ({ endTime, onExamComplete }) => {
  // State for time remaining and timer completion
  const [timeLeft, setTimeLeft] = useState("");
  const [isTimeUp, setIsTimeUp] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const examEndTime = new Date(endTime).getTime();
      const difference = examEndTime - now;

      if (difference <= 0) {
        // Time's up - auto submit if no offline answers
        handleAutoSubmit();
        return "Time's up!";
      }

      const hours = Math.floor(difference / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
    };

    const handleAutoSubmit = async () => {
      try {
        console.log("[TIMER] Auto-submitting exam due to time expiry");

        // Mark exam as completed on the server
        const setEndExamResponse = await fetch(
          "https://procbackend.elcarreira.com/students/setEndExam",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              examId: window.appState.examId,
              studentId: window.appState.clientId,
              endReason: "TIMEOUT",
            }),
          }
        );

        const endExamData = await setEndExamResponse.json();
        console.log("[TIMER] setEndExam response:", endExamData);

        if (!setEndExamResponse.ok || !endExamData.message?.acknowledged) {
          throw new Error("Failed to mark exam as completed");
        }
        console.log("[TIMER] Successfully marked exam as completed");

        // Clear localStorage and end exam
        localStorage.removeItem("examAnswers");

        // Update appState
        window.appState = {
          ...window.appState,
          mode: "EXAM_COMPLETE",
          examEndReason: "TIMEOUT",
          isExamStarted: false,
        };

        window.dispatchEvent(
          new CustomEvent("examStateChange", {
            detail: {
              mode: "EXAM_COMPLETE",
              reason: "TIMEOUT",
              timestamp: Date.now(),
            },
          })
        );

        // Call handleSessionEnd
        if (window.handleSessionEnd) {
          console.log("[TIMER] Calling handleSessionEnd with reason: TIMEOUT");
          window.handleSessionEnd("TIMEOUT");
        }
      } catch (error) {
        console.error("[TIMER] Auto-submit error:", error);
        // Even if submission fails, we need to end the exam
        window.appState.mode = "EXAM_COMPLETE";
        window.appState.examEndReason = "TIMEOUT";
        window.dispatchEvent(
          new CustomEvent("examStateChange", {
            detail: {
              mode: "EXAM_COMPLETE",
              reason: "TIMEOUT",
              timestamp: Date.now(),
            },
          })
        );

        // Call onExamComplete in error case
        if (onExamComplete) {
          onExamComplete();
        }
      }
    };

    const timer = setInterval(() => {
      const timeLeftStr = calculateTimeLeft();
      setTimeLeft(timeLeftStr);

      if (timeLeftStr === "Time's up!") {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [endTime, onExamComplete]);

  const handleWarningClose = () => {
    setShowWarning(false);
    if (onExamComplete) {
      onExamComplete();
    }
  };

  // Auto transition after 3 seconds when warning is shown
  useEffect(() => {
    if (showWarning) {
      const autoTransition = setTimeout(() => {
        handleWarningClose();
      }, 3000);
      return () => clearTimeout(autoTransition);
    }
  }, [showWarning]);

  return (
    <>
      <div className={`exam-timer ${isTimeUp ? "time-up" : ""}`}>
        <span className="timer-icon">⏱️</span>
        <span className="timer-text">Time Remaining: {timeLeft}</span>
      </div>
      <WarningDialog
        isOpen={showWarning}
        onClose={handleWarningClose}
        message="Exam time is up! You will be redirected to answer upload shortly."
        warningCount={1}
        totalWarnings={1}
      />
    </>
  );
};

export default ExamTimer;
